import { AppClient, IFilesDict, IJob } from '@biolibtech/biolib-js';
import { IModuleInput } from '@biolibtech/biolib-js/src/AppClient/biolibBinaryFormat/ModuleInput';
import { dispatchMutation } from '../../state';
import { jobsApi } from '../../apiHooks/jobsApi';

export enum OpenResultTarget {
    CurrentWindow = 'currentWindow',
    NewTab = 'newTab',
}

export interface IRunJobPayload {
    appUri: string;
    arguments: string[];
    files: IFilesDict;
    temporaryClientSecrets?: Record<string, string>;
    openResult?: boolean | OpenResultTarget;
    blocking?: boolean;
    experimentUuid?: string;
    resultNamePrefix?: string;
    machine?: string;
}

export interface IJobResult {
    exit_code: number;
    stderr: Uint8Array;
    stdout: Uint8Array;
    files: IFilesDict;
    jobId: string;
}

export interface IPendingJobResult {
    jobId: string;
    authToken: string;
    portEndpointUrls?: string[];
}

export interface IJobProgress {
    logMessage?: string;
    systemLogMessage?: string;
    jobId: string;
}

export async function runJob(
    payload: IRunJobPayload,
    jobProgressHandler: (jobProgress: IJobProgress) => void,
): Promise<IJobResult | IPendingJobResult> {
    if (payload.openResult && payload.blocking === true) {
        throw new Error('openResult cannot be used with blocking: true');
    }

    const input: IModuleInput = {
        files: payload.files,
        parameters: payload.arguments,
    };

    const job = await dispatchMutation(jobsApi.endpoints.createJob, {
        appUri: payload.appUri,
        temporaryClientSecrets: payload.temporaryClientSecrets,
        experimentUuid: payload.experimentUuid,
        machine: payload.machine,
    });
    const jobUuid = job.public_id;

    if (payload.openResult || payload.blocking === false) {
        await dispatchMutation(jobsApi.endpoints.startJob, {
            job,
            moduleInput: input,
            resultNamePrefix: payload.resultNamePrefix,
        });
        if (payload.openResult) {
            const resultUrl = `/results/${jobUuid}/`;
            if (payload.openResult === OpenResultTarget.NewTab) {
                window.open(resultUrl, '_blank', 'noopener,noreferrer');
            } else {
                location.href = resultUrl;
            }
        }
        const { main_output_port: mainOutputPort, results_proxy_hostname: resultsProxyHostname } = job.app_version;
        const mainModule = job.app_version.modules?.find((module) => module.name === 'main');
        const liveEndpointPorts = new Set<number>();
        if (mainOutputPort) {
            liveEndpointPorts.add(mainOutputPort);
        }
        for (const port of mainModule?.ports ?? []) {
            liveEndpointPorts.add(port);
        }
        const portEndpointUrls =
            resultsProxyHostname && liveEndpointPorts.size > 0
                ? [...liveEndpointPorts].map((port) => `https://${jobUuid}-${port}.${resultsProxyHostname}`)
                : undefined;
        return { jobId: jobUuid, authToken: job.auth_token, portEndpointUrls };
    }

    jobProgressHandler({ jobId: jobUuid, systemLogMessage: 'Created job' });

    const addLogMessage = (systemLogMessage: string) => {
        console.log(`Job "${jobUuid}" addLogMessage: ${systemLogMessage}`);
        jobProgressHandler({ jobId: jobUuid, systemLogMessage });
    };

    const addStreamingOutputMessage = (logMessage: string) => {
        console.log(`Job "${jobUuid}" addStreamingOutputMessage: ${logMessage}`);
        jobProgressHandler({ jobId: jobUuid, logMessage });
    };

    const setProgressInitialization = (progress: number) => {
        console.log(`Job "${jobUuid}" setProgressInitialization: ${progress}`);
    };

    const setProgressCompute = (progress: number) => {
        console.log(`Job "${jobUuid}" setProgressCompute: ${progress}`);
    };

    const moduleOutput = await AppClient.runJob(job, input, {
        addLogMessage,
        addStreamingOutputMessage,
        setProgressCompute,
        setProgressInitialization,
    }, payload.resultNamePrefix);

    const files: IFilesDict = {};
    const sizeInBytes = await moduleOutput.getSizeInBytes();
    if (sizeInBytes < 30_000_000) {
        await moduleOutput.fetchAllData();
        const outputFiles = await moduleOutput.getFiles();
        for (const file of outputFiles) {
            files[file.path] = { data: await file.getData() };
        }
    } else {
        console.warn('Result files not loaded for job in iFrame');
    }

    const exit_code = await moduleOutput.getExitCode();
    const stderr = await moduleOutput.getStderr();
    const stdout = await moduleOutput.getStdout();
    return { exit_code, stderr, stdout, files, jobId: jobUuid };
}
